import { useCallback, useEffect, useState } from "react";

/**
 * The player's chosen discovery center (facility-discovery B1, ADR-024).
 *
 * A coarse point — either a precise device fix (adult opt-in) or a geocoded
 * area — that the discovery surfaces (`/dropin`, `/book`) sort distance from.
 * Held CLIENT-SIDE only (localStorage, mirrored across tabs); NOTHING is written
 * to the server in Phase 1 (ADR-024 §"Persistence is opt-in and coarse, Phase 3
 * only"). No precise device coordinates are ever persisted server-side.
 *
 * `null` center = no location chosen → the surfaces request no distance and show
 * none (the honesty guard: no fabricated proximity without a center).
 */
export interface NearbyCenter {
  latitude: number;
  longitude: number;
  /** Optional human label for the area chip ("Near Vancouver"). */
  label?: string;
  /**
   * True when this center is a COARSE area centroid (e.g. seeded from the
   * player's saved home area), as opposed to a precise device fix. A coarse
   * center is safe to thread into discovery even for an unconsented minor
   * (ADR-024 §"Minor gate" — it is an area centroid, not a precise fix), so the
   * discovery surfaces gate the PRECISE device path on consent but always allow
   * a coarse center. Absent/false = treat as possibly-precise (gated).
   */
  coarse?: boolean;
}

const STORAGE_KEY = "bw.player.nearbyCenter";

function readStored(): NearbyCenter | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as unknown;
    if (
      parsed &&
      typeof parsed === "object" &&
      typeof (parsed as NearbyCenter).latitude === "number" &&
      typeof (parsed as NearbyCenter).longitude === "number"
    ) {
      return parsed as NearbyCenter;
    }
  } catch {
    // Corrupt / unavailable storage → behave as "no center".
  }
  return null;
}

/**
 * Read + set the discovery center. Persists to localStorage and keeps every hook
 * instance (and browser tab) in sync, so the chip on `/dropin` and `/book` share
 * one center without a server round-trip.
 */
export function useNearbyCenter(): {
  center: NearbyCenter | null;
  setCenter: (center: NearbyCenter) => void;
  clearCenter: () => void;
} {
  const [center, setCenterState] = useState<NearbyCenter | null>(() =>
    readStored(),
  );

  // Cross-tab + cross-instance sync: a write in one place updates the others.
  useEffect(() => {
    function onStorage(e: StorageEvent) {
      if (e.key === STORAGE_KEY) {
        setCenterState(readStored());
      }
    }
    function onLocal() {
      setCenterState(readStored());
    }
    window.addEventListener("storage", onStorage);
    window.addEventListener("bw:nearby-center", onLocal);
    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("bw:nearby-center", onLocal);
    };
  }, []);

  const setCenter = useCallback((next: NearbyCenter) => {
    setCenterState(next);
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    } catch {
      // Best-effort persistence; the in-memory value still drives this session.
    }
    // Notify same-tab instances (the native `storage` event only fires in OTHER
    // tabs), so a chip on the same page re-reads the new center immediately.
    window.dispatchEvent(new Event("bw:nearby-center"));
  }, []);

  const clearCenter = useCallback(() => {
    setCenterState(null);
    try {
      window.localStorage.removeItem(STORAGE_KEY);
    } catch {
      // ignore
    }
    window.dispatchEvent(new Event("bw:nearby-center"));
  }, []);

  return { center, setCenter, clearCenter };
}
